import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';

interface CopyStrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (strategy: CopyStrategy) => void;
  groupName?: string;
}

export interface CopyStrategy {
  id: string;
  name: string;
  groupName: string;
  autoBuy: {
    enabled: boolean;
    amount: number;
    minFollowers: number;
    minAmount: number;
  };
  tokenFilters: string[];
  autoSell: {
    sellOnFollowerSell: boolean;
    sellOnGainPercent: number;
    sellOnDropPercent: number;
    sellOnGainEnabled: boolean;
    sellOnDropEnabled: boolean;
  };
  createdAt: string;
}

const CopyStrategyModal: React.FC<CopyStrategyModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  groupName = "Selected Group" 
}) => {
  const [strategy, setStrategy] = useState<CopyStrategy>({
    id: '',
    name: `Copy ${groupName}`,
    groupName,
    autoBuy: {
      enabled: true,
      amount: 25,
      minFollowers: 1,
      minAmount: 500
    },
    tokenFilters: [],
    autoSell: {
      sellOnFollowerSell: true,
      sellOnGainPercent: 50,
      sellOnDropPercent: 18,
      sellOnGainEnabled: true,
      sellOnDropEnabled: true
    },
    createdAt: new Date().toISOString()
  });

  const [newFilter, setNewFilter] = useState('');

  if (!isOpen) return null;

  const handleSave = () => {
    const savedStrategy = {
      ...strategy,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    onSave(savedStrategy);
    onClose();
  };

  const addTokenFilter = () => {
    if (newFilter.trim() && !strategy.tokenFilters.includes(newFilter.trim())) {
      setStrategy(prev => ({
        ...prev,
        tokenFilters: [...prev.tokenFilters, newFilter.trim()]
      }));
      setNewFilter('');
    }
  };

  const removeTokenFilter = (filter: string) => {
    setStrategy(prev => ({
      ...prev,
      tokenFilters: prev.tokenFilters.filter(f => f !== filter)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-green-400">Set your quick copy strategy</h2>
            <Button
              onClick={onClose}
              className="bg-transparent hover:bg-red-500/20 text-red-400 p-2"
            >
              ✕
            </Button>
          </div>

          <div className="space-y-6">
            {/* Strategy Name */}
            <div>
              <label className="block text-green-400 text-sm font-medium mb-2">
                Strategy Name
              </label>
              <input
                type="text"
                value={strategy.name}
                onChange={(e) => setStrategy(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-green-400 placeholder-green-400/50 focus:border-green-400 focus:outline-none"
                placeholder="Enter strategy name"
              />
            </div>

            {/* AUTO-BUY Section */}
            <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
              <h3 className="text-lg font-bold text-blue-300 mb-4">AUTO-BUY</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-green-400/70 text-sm mb-1">Buy Amount ($)</label>
                  <input
                    type="number"
                    value={strategy.autoBuy.amount}
                    onChange={(e) => setStrategy(prev => ({
                      ...prev,
                      autoBuy: { ...prev.autoBuy, amount: Number(e.target.value) }
                    }))}
                    className="w-full bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-green-400 focus:border-green-400 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-green-400/70 text-sm mb-1">Min Followers</label>
                  <input
                    type="number"
                    value={strategy.autoBuy.minFollowers}
                    onChange={(e) => setStrategy(prev => ({
                      ...prev,
                      autoBuy: { ...prev.autoBuy, minFollowers: Number(e.target.value) }
                    }))}
                    className="w-full bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-green-400 focus:border-green-400 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-green-400/70 text-sm mb-1">Min Amount ($)</label>
                  <input
                    type="number"
                    value={strategy.autoBuy.minAmount}
                    onChange={(e) => setStrategy(prev => ({
                      ...prev,
                      autoBuy: { ...prev.autoBuy, minAmount: Number(e.target.value) }
                    }))}
                    className="w-full bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-green-400 focus:border-green-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="text-green-400/80 text-sm">
                Buy me <span className="font-bold">${strategy.autoBuy.amount}</span> worth of tokens whenever{' '}
                <span className="font-bold">{strategy.autoBuy.minFollowers}</span> person in{' '}
                <span className="text-blue-300 font-bold">&lt;{groupName}&gt;</span> buys a token with at least{' '}
                <span className="font-bold">${strategy.autoBuy.minAmount}</span>
              </div>
            </div>

            {/* TOKEN FILTERS Section */}
            <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20">
              <h3 className="text-lg font-bold text-purple-300 mb-4">TOKEN FILTERS</h3>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newFilter}
                  onChange={(e) => setNewFilter(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTokenFilter()}
                  placeholder="Add token symbol (e.g., BTC, ETH)"
                  className="flex-1 bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-green-400 placeholder-green-400/50 focus:border-green-400 focus:outline-none"
                />
                <Button
                  onClick={addTokenFilter}
                  className="bg-purple-600/30 hover:bg-purple-500/40 text-purple-300 border border-purple-500/40 px-4"
                >
                  + Add
                </Button>
              </div>

              {strategy.tokenFilters.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {strategy.tokenFilters.map((filter, index) => (
                    <div
                      key={index}
                      className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      {filter}
                      <button
                        onClick={() => removeTokenFilter(filter)}
                        className="text-red-400 hover:text-red-300"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* AUTO-SELL Section */}
            <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
              <h3 className="text-lg font-bold text-red-300 mb-4">AUTO-SELL</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-red-500/5 rounded border border-red-500/20">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🔄</span>
                    <span className="text-red-300">When they sell 50% of it</span>
                  </div>
                  <button
                    onClick={() => setStrategy(prev => ({
                      ...prev,
                      autoSell: { ...prev.autoSell, sellOnFollowerSell: !prev.autoSell.sellOnFollowerSell }
                    }))}
                    className={`w-8 h-8 rounded ${
                      strategy.autoSell.sellOnFollowerSell 
                        ? 'bg-green-500 text-white' 
                        : 'bg-red-500/30 text-red-400'
                    }`}
                  >
                    {strategy.autoSell.sellOnFollowerSell ? '✓' : '✕'}
                  </button>
                </div>

                <div className="flex items-center justify-between p-3 bg-red-500/5 rounded border border-red-500/20">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📈</span>
                    <span className="text-red-300">Or the token gains</span>
                    <input
                      type="number"
                      value={strategy.autoSell.sellOnGainPercent}
                      onChange={(e) => setStrategy(prev => ({
                        ...prev,
                        autoSell: { ...prev.autoSell, sellOnGainPercent: Number(e.target.value) }
                      }))}
                      className="w-16 bg-green-500/10 border border-green-500/30 rounded px-2 py-1 text-green-400 text-center focus:border-green-400 focus:outline-none"
                    />
                    <span className="text-red-300">%</span>
                  </div>
                  <button
                    onClick={() => setStrategy(prev => ({
                      ...prev,
                      autoSell: { ...prev.autoSell, sellOnGainEnabled: !prev.autoSell.sellOnGainEnabled }
                    }))}
                    className={`w-8 h-8 rounded ${
                      strategy.autoSell.sellOnGainEnabled 
                        ? 'bg-green-500 text-white' 
                        : 'bg-red-500/30 text-red-400'
                    }`}
                  >
                    {strategy.autoSell.sellOnGainEnabled ? '✓' : '✕'}
                  </button>
                </div>

                <div className="flex items-center justify-between p-3 bg-red-500/5 rounded border border-red-500/20">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📉</span>
                    <span className="text-red-300">Or the token drops by</span>
                    <input
                      type="number"
                      value={strategy.autoSell.sellOnDropPercent}
                      onChange={(e) => setStrategy(prev => ({
                        ...prev,
                        autoSell: { ...prev.autoSell, sellOnDropPercent: Number(e.target.value) }
                      }))}
                      className="w-16 bg-green-500/10 border border-green-500/30 rounded px-2 py-1 text-green-400 text-center focus:border-green-400 focus:outline-none"
                    />
                    <span className="text-red-300">%</span>
                  </div>
                  <button
                    onClick={() => setStrategy(prev => ({
                      ...prev,
                      autoSell: { ...prev.autoSell, sellOnDropEnabled: !prev.autoSell.sellOnDropEnabled }
                    }))}
                    className={`w-8 h-8 rounded ${
                      strategy.autoSell.sellOnDropEnabled 
                        ? 'bg-green-500 text-white' 
                        : 'bg-red-500/30 text-red-400'
                    }`}
                  >
                    {strategy.autoSell.sellOnDropEnabled ? '✓' : '✕'}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={onClose}
                className="flex-1 bg-gray-600/30 hover:bg-gray-500/40 text-gray-300 border border-gray-500/40"
              >
                Discard
              </Button>
              <Button
                onClick={handleSave}
                className="flex-1 bg-blue-600/30 hover:bg-blue-500/40 text-blue-300 border border-blue-500/40"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CopyStrategyModal;
